import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Search, Mail, Shield } from 'lucide-react';

interface User {
  user_id: string;
  business_email: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  is_platform_admin: boolean;
  platform_admin_role: string | null;
  team_count: number;
}

export function UserManagement() {
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all users via RPC
  const { data: allUsers, isLoading } = useQuery({
    queryKey: ['platform-admin-users'],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_all_users_for_platform_admin');
      
      if (error) throw error;
      return (data || []) as User[];
    }
  });

  // Filter in memory for search
  const users = searchQuery 
    ? allUsers?.filter(user => {
        const search = searchQuery.toLowerCase();
        return (
          user.business_email?.toLowerCase().includes(search) ||
          user.legal_first_name?.toLowerCase().includes(search) ||
          user.legal_last_name?.toLowerCase().includes(search)
        );
      })
    : allUsers;

  return (
    <Card>
      <CardHeader>
        <CardTitle>User Management</CardTitle>
        <CardDescription>
          Manage platform users across all organizations
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by email or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading users...
          </div>
        ) : !users || users.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery ? 'No users found matching your search' : 'No users found'}
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Teams</TableHead>
                  <TableHead>Admin Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell className="font-medium">
                      {user.legal_first_name && user.legal_last_name
                        ? `${user.legal_first_name} ${user.legal_last_name}`
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {user.business_email}
                      </div>
                    </TableCell>
                    <TableCell>{user.team_count}</TableCell>
                    <TableCell>
                      {user.is_platform_admin && user.platform_admin_role ? (
                        <Badge variant="secondary" className="gap-1">
                          <Shield className="h-3 w-3" />
                          {user.platform_admin_role}
                        </Badge>
                      ) : (
                        <Badge variant="outline">Regular User</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
